import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Eye, Plus, Trash2, ChevronDown, ChevronUp, Upload, Film, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;

const defaultSettings = {
  hero_headline: '',
  hero_subheadline: '',
  hero_cta_text: '',
  hero_cta_link: '',
  features: [],
  how_it_works_steps: [],
  benefits: [],
  about_headline: '',
  about_text: '',
  primary_color: '#3A7BFF',
  secondary_color: '#39E6C8',
  accent_color: '#FF5F6D',
  show_features: true,
  show_how_it_works: true,
  show_benefits: true,
  show_about: true,
  demo_video_url: '',
  show_demo_video: false,
  demo_video_updated_at: null
};

export default function AdminLandingPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingDemo, setUploadingDemo] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    features: false,
    howItWorks: false,
    benefits: false,
    about: false,
    brand: false,
    visibility: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/landing-page`, {
        withCredentials: true
      });
      setSettings({ ...defaultSettings, ...(response.data || {}) });
    } catch (err) {
      console.error('Error loading settings:', err);
      toast.error('Failed to load landing page settings');
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...settings, demo_video_updated_at: settings.demo_video_updated_at || new Date().toISOString() };
      await axios.post(
        `${API_URL}/api/admin/landing-page/update`,
        payload,
        { withCredentials: true }
      );
      localStorage.setItem('admin_landing_settings_cache', JSON.stringify(payload));
      toast.success('Landing page settings saved successfully!');
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedDefault = async () => {
    try {
      await axios.post(
        `${API_URL}/api/admin/landing-page/seed`,
        {},
        { withCredentials: true }
      );
      toast.success('Default settings seeded!');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleDemoVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingDemo(true);
    try {
      // UI-side “App Storage” pattern: create a local object URL and persist the reference
      const objectUrl = URL.createObjectURL(file);
      const savedAt = new Date().toISOString();
      setSettings((prev) => ({
        ...prev,
        demo_video_url: objectUrl,
        show_demo_video: true,
        demo_video_updated_at: savedAt
      }));
      toast.success('Demo video staged for save');
    } catch (err) {
      console.error('Demo upload failed', err);
      toast.error('Failed to stage demo video');
    } finally {
      setUploadingDemo(false);
      event.target.value = '';
    }
  };

  const addFeature = () => {
    setSettings(prev => ({
      ...prev,
      features: [...prev.features, { icon: '🎯', title: '', description: '' }]
    }));
  };

  const removeFeature = (index) => {
    setSettings(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const addStep = () => {
    const nextStep = settings.how_it_works_steps.length + 1;
    setSettings(prev => ({
      ...prev,
      how_it_works_steps: [...prev.how_it_works_steps, { step: nextStep, title: '', description: '' }]
    }));
  };

  const removeStep = (index) => {
    setSettings(prev => ({
      ...prev,
      how_it_works_steps: prev.how_it_works_steps.filter((_, i) => i !== index)
    }));
  };

  const updateStep = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      how_it_works_steps: prev.how_it_works_steps.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const addBenefit = () => {
    setSettings(prev => ({
      ...prev,
      benefits: [...prev.benefits, '']
    }));
  };

  const removeBenefit = (index) => {
    setSettings(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const updateBenefit = (index, value) => {
    setSettings(prev => ({
      ...prev,
      benefits: prev.benefits.map((b, i) => i === index ? value : b)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-400 mb-4">No landing page settings found</p>
        <button
          onClick={handleSeedDefault}
          className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
        >
          Create Default Settings
        </button>
      </div>
    );
  }

  const renderDemoStatus = () => {
    if (!settings.demo_video_url) {
      return <span className="text-sm text-[#8A93A6]">No demo video uploaded</span>;
    }
    return (
      <div className="flex items-center gap-2 text-sm text-green-400">
        <CheckCircle className="h-4 w-4" />
        <span>Demo video saved{settings.demo_video_updated_at ? ` • ${new Date(settings.demo_video_updated_at).toLocaleString()}` : ''}</span>
      </div>
    );
  };

  return (
    <div className="h-full overflow-auto bg-[#121417]">
      <div className="max-w-5xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Landing Page Editor</h1>
            <p className="text-gray-400">Customize your public landing page</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview
            </a>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Hero Section */}
        <Section
          title="Hero Section"
          expanded={expandedSections.hero}
          onToggle={() => toggleSection('hero')}
        >
          <div className="space-y-4">
            <InputField
              label="Headline"
              value={settings.hero_headline}
              onChange={(e) => setSettings({ ...settings, hero_headline: e.target.value })}
              placeholder="Your main headline"
            />
            <TextareaField
              label="Subheadline"
              value={settings.hero_subheadline}
              onChange={(e) => setSettings({ ...settings, hero_subheadline: e.target.value })}
              placeholder="Supporting text"
            />
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="CTA Button Text"
                value={settings.hero_cta_text}
                onChange={(e) => setSettings({ ...settings, hero_cta_text: e.target.value })}
                placeholder="Get Started"
              />
              <InputField
                label="CTA Button Link"
                value={settings.hero_cta_link}
                onChange={(e) => setSettings({ ...settings, hero_cta_link: e.target.value })}
                placeholder="/signup"
              />
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Demo Video</p>
                  <p className="text-sm text-[#8A93A6]">Upload a demo video to feature on the public landing page</p>
                </div>
                <label className="flex items-center gap-2 px-4 py-2 bg-[#1a1d23] border border-gray-800 rounded-lg cursor-pointer hover:border-cyan-600">
                  <Upload className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-white">{uploadingDemo ? 'Uploading...' : 'Upload'}</span>
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={handleDemoVideoUpload}
                    disabled={uploadingDemo}
                  />
                </label>
              </div>

              {settings.demo_video_url && (
                <div className="p-4 bg-[#1a1d23] border border-gray-800 rounded-lg space-y-2">
                  <div className="flex items-center gap-2 text-sm text-[#8A93A6] break-all">
                    <Film className="w-4 h-4 text-cyan-400" />
                    <span>{settings.demo_video_url}</span>
                  </div>
                  <video controls className="w-full rounded-lg" src={settings.demo_video_url} />
                </div>
              )}

              <ToggleField
                label="Show Demo Video on Public Landing Page"
                checked={settings.show_demo_video}
                onChange={(checked) => setSettings({ ...settings, show_demo_video: checked })}
              />
              {renderDemoStatus()}
            </div>
          </div>
        </Section>

        {/* Features Section */}
        <Section
          title="Features Section"
          expanded={expandedSections.features}
          onToggle={() => toggleSection('features')}
        >
          <div className="space-y-4">
            {settings.features.map((feature, idx) => (
              <div key={idx} className="p-4 bg-[#1a1d23] rounded-lg border border-gray-800">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-gray-400 text-sm">Feature {idx + 1}</span>
                  <button
                    onClick={() => removeFeature(idx)}
                    className="text-red-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <InputField
                    label="Icon (emoji)"
                    value={feature.icon}
                    onChange={(e) => updateFeature(idx, 'icon', e.target.value)}
                    placeholder="⚡"
                  />
                  <InputField
                    label="Title"
                    value={feature.title}
                    onChange={(e) => updateFeature(idx, 'title', e.target.value)}
                    placeholder="Feature Title"
                  />
                  <InputField
                    label="Description"
                    value={feature.description}
                    onChange={(e) => updateFeature(idx, 'description', e.target.value)}
                    placeholder="Feature description"
                  />
                </div>
              </div>
            ))}
            <button
              onClick={addFeature}
              className="w-full py-2 border-2 border-dashed border-gray-700 hover:border-cyan-600 rounded-lg text-gray-400 hover:text-cyan-500 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Feature
            </button>
          </div>
        </Section>

        {/* How It Works Section */}
        <Section
          title="How It Works Section"
          expanded={expandedSections.howItWorks}
          onToggle={() => toggleSection('howItWorks')}
        >
          <div className="space-y-4">
            {settings.how_it_works_steps.map((step, idx) => (
              <div key={idx} className="p-4 bg-[#1a1d23] rounded-lg border border-gray-800">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-gray-400 text-sm">Step {step.step}</span>
                  <button
                    onClick={() => removeStep(idx)}
                    className="text-red-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    label="Title"
                    value={step.title}
                    onChange={(e) => updateStep(idx, 'title', e.target.value)}
                    placeholder="Step Title"
                  />
                  <InputField
                    label="Description"
                    value={step.description}
                    onChange={(e) => updateStep(idx, 'description', e.target.value)}
                    placeholder="Step description"
                  />
                </div>
              </div>
            ))}
            <button
              onClick={addStep}
              className="w-full py-2 border-2 border-dashed border-gray-700 hover:border-cyan-600 rounded-lg text-gray-400 hover:text-cyan-500 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Step
            </button>
          </div>
        </Section>

        {/* Benefits Section */}
        <Section
          title="Benefits Section"
          expanded={expandedSections.benefits}
          onToggle={() => toggleSection('benefits')}
        >
          <div className="space-y-4">
            {settings.benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <input
                  type="text"
                  value={benefit}
                  onChange={(e) => updateBenefit(idx, e.target.value)}
                  className="flex-1 bg-[#1a1d23] border border-gray-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-600"
                  placeholder="Benefit description"
                />
                <button
                  onClick={() => removeBenefit(idx)}
                  className="text-red-500 hover:text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={addBenefit}
              className="w-full py-2 border-2 border-dashed border-gray-700 hover:border-cyan-600 rounded-lg text-gray-400 hover:text-cyan-500 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Benefit
            </button>
          </div>
        </Section>

        {/* About Section */}
        <Section
          title="About Section"
          expanded={expandedSections.about}
          onToggle={() => toggleSection('about')}
        >
          <div className="space-y-4">
            <InputField
              label="About Headline"
              value={settings.about_headline}
              onChange={(e) => setSettings({ ...settings, about_headline: e.target.value })}
              placeholder="About your platform"
            />
            <TextareaField
              label="About Text"
              value={settings.about_text}
              onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
              placeholder="Tell your story"
              rows={4}
            />
          </div>
        </Section>

        {/* Brand Colors */}
        <Section
          title="Brand Colors"
          expanded={expandedSections.brand}
          onToggle={() => toggleSection('brand')}
        >
          <div className="grid grid-cols-3 gap-4">
            <ColorField
              label="Primary Color"
              value={settings.primary_color}
              onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
            />
            <ColorField
              label="Secondary Color"
              value={settings.secondary_color}
              onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
            />
            <ColorField
              label="Accent Color"
              value={settings.accent_color}
              onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
            />
          </div>
        </Section>

        {/* Section Visibility */}
        <Section
          title="Section Visibility"
          expanded={expandedSections.visibility}
          onToggle={() => toggleSection('visibility')}
        >
          <div className="space-y-3">
            <ToggleField
              label="Show Features"
              checked={settings.show_features}
              onChange={(checked) => setSettings({ ...settings, show_features: checked })}
            />
            <ToggleField
              label="Show How It Works"
              checked={settings.show_how_it_works}
              onChange={(checked) => setSettings({ ...settings, show_how_it_works: checked })}
            />
            <ToggleField
              label="Show Benefits"
              checked={settings.show_benefits}
              onChange={(checked) => setSettings({ ...settings, show_benefits: checked })}
            />
            <ToggleField
              label="Show About"
              checked={settings.show_about}
              onChange={(checked) => setSettings({ ...settings, show_about: checked })}
            />
          </div>
        </Section>

        {/* Save Button (Bottom) */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper Components
function Section({ title, expanded, onToggle, children }) {
  return (
    <div className="mb-6 bg-[#1a1d23] rounded-lg border border-gray-800 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#1f2329] transition-colors"
      >
        <h2 className="text-xl font-bold text-white">{title}</h2>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {expanded && (
        <div className="px-6 pb-6">
          {children}
        </div>
      )}
    </div>
  );
}

function InputField({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-400 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-[#1a1d23] border border-gray-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-600"
      />
    </div>
  );
}

function TextareaField({ label, value, onChange, placeholder, rows = 3 }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-400 mb-2">{label}</label>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className="w-full bg-[#1a1d23] border border-gray-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-600 resize-none"
      />
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-400 mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={onChange}
          className="w-12 h-10 rounded cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={onChange}
          className="flex-1 bg-[#1a1d23] border border-gray-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-600"
        />
      </div>
    </div>
  );
}

function ToggleField({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-white">{label}</label>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          checked ? 'bg-cyan-600' : 'bg-gray-700'
        }`}
      >
        <div
          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
            checked ? 'transform translate-x-6' : ''
          }`}
        />
      </button>
    </div>
  );
}
